import { applyOptionCombinations } from './option-combinations.js'

const FORM_SELECTOR = '[data-product-configurator]'
const OPTION_SELECTOR = '[data-product-configurator-option]'
const REQUIRED_OPTION_SELECTOR = `${OPTION_SELECTOR}[data-product-configurator-required="true"]`
const VALIDATION_FIELD_SELECTOR = '[data-product-configurator-validation-field]'
const VALIDATION_TRIGGER_SELECTOR = '[data-product-configurator-validation-trigger]'
const VALIDATION_MESSAGE_SELECTOR = '[data-product-configurator-validation-message]'
const PRICE_SELECTOR = '[data-product-configurator-price]'
const ORIGINAL_PRICE_SELECTOR = '[data-product-configurator-original-price]'
const DISCOUNT_SELECTOR = '[data-product-configurator-discount]'
const AVAILABILITY_SELECTOR = '[data-product-configurator-availability]'
//const AVAILABILITY_TEXT_SELECTOR = '[data-product-configurator-availability-text]'
const LOADING_SELECTOR = '[data-product-configurator-loading]'
const AMOUNT_SELECTOR = '[data-product-configurator-amount]'
//const PRIMARY_OPTION_SELECTOR = '[data-primary-option-value]:checked'
const PRICE_CONTAINER_SELECTOR = '[data-product-configurator-price-container]'
const STOCK_REMINDER_SELECTOR = '[data-product-stock-reminder]'

export default class ProductConfigurator {
  constructor() {
    this.abortControllers = new WeakMap()
    this.updateTimers = new WeakMap()
    this.changedOptionForms = new WeakSet()

    this.handleChange = this.handleChange.bind(this)
    this.handleInput = this.handleInput.bind(this)
    this.handleModalContentLoaded = this.handleModalContentLoaded.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  init() {
    document.addEventListener('change', this.handleChange)
    document.addEventListener('input', this.handleInput)
    document.addEventListener('remote-modal:content-loaded', this.handleModalContentLoaded)
    document.addEventListener('submit', this.handleSubmit, true)
    document.querySelectorAll(FORM_SELECTOR).forEach((form) => this.initForm(form))
  }

  initForm(form) {
    if (form instanceof HTMLFormElement) {
      applyOptionCombinations(form)
    }

    this.update(form)
  }

  handleModalContentLoaded(event) {
    if (!(event.target instanceof Element)) {
      return
    }

    event.target.querySelectorAll(FORM_SELECTOR).forEach((form) => this.initForm(form))
  }

  handleChange(event) {
    if (!(event.target instanceof Element)) {
      return
    }

    const form = event.target.closest(FORM_SELECTOR)

    if (!(form instanceof HTMLFormElement)) {
      return
    }

    if (event.target.matches(OPTION_SELECTOR)) {
      this.clearValidationState(form)
      this.changedOptionForms.add(form)

      applyOptionCombinations(form, event.target)
    }

    const isOptionField = event.target.matches(OPTION_SELECTOR)
    const isAmountField = event.target.matches(AMOUNT_SELECTOR)

    if (!isOptionField && !isAmountField) {
      return
    }

    this.scheduleUpdate(form, isAmountField ? 250 : 0)
  }

  handleInput(event) {
    if (!(event.target instanceof Element)) {
      return
    }

    if (!event.target.matches(AMOUNT_SELECTOR)) {
      return
    }

    const form = event.target.closest(FORM_SELECTOR)

    if (!(form instanceof HTMLFormElement)) {
      return
    }

    /*
     * Bij typen in het aantal niet na iedere toetsaanslag
     * onmiddellijk een request sturen.
     */
    this.scheduleUpdate(form, 300)
  }

  handleSubmit(event) {
    if (!(event.target instanceof HTMLFormElement)) {
      return
    }

    const form = event.target

    if (!form.matches(FORM_SELECTOR)) {
      return
    }

    const incompleteOption = this.findIncompleteRequiredOption(form)

    if (!(incompleteOption instanceof HTMLElement)) {
      this.clearValidationState(form)

      return
    }

    const validationField = incompleteOption.closest(VALIDATION_FIELD_SELECTOR)

    if (!(validationField instanceof HTMLElement)) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    this.showValidationError(validationField)
  }

  findIncompleteRequiredOption(form) {
    const requiredOptions = [...form.querySelectorAll(REQUIRED_OPTION_SELECTOR)]

    for (const option of requiredOptions) {
      if (option instanceof HTMLInputElement && option.type === 'radio') {
        const checked = requiredOptions.some(
          (candidate) => candidate instanceof HTMLInputElement && candidate.type === 'radio' && candidate.name === option.name && candidate.checked,
        )

        if (!checked) {
          return option
        }

        continue
      }

      if ('value' in option && !String(option.value).trim()) {
        return option
      }
    }

    return null
  }

  showValidationError(field) {
    const trigger = field.querySelector(VALIDATION_TRIGGER_SELECTOR)
    const message = field.querySelector(VALIDATION_MESSAGE_SELECTOR)

    trigger?.classList.add('border-danger')
    trigger?.setAttribute('aria-invalid', 'true')

    message?.classList.remove('d-none')

    if (trigger instanceof HTMLElement) {
      trigger.focus({ preventScroll: true })
    }

    if (message instanceof HTMLElement) {
      message.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }

  clearValidationState(form) {
    form.querySelectorAll(VALIDATION_FIELD_SELECTOR).forEach((field) => {
      const trigger = field.querySelector(VALIDATION_TRIGGER_SELECTOR)
      const message = field.querySelector(VALIDATION_MESSAGE_SELECTOR)

      trigger?.classList.remove('border-danger')
      trigger?.removeAttribute('aria-invalid')

      message?.classList.add('d-none')
    })
  }

  scheduleUpdate(form, delay) {
    const currentTimer = this.updateTimers.get(form)

    if (currentTimer) {
      window.clearTimeout(currentTimer)
    }

    const timer = window.setTimeout(() => {
      this.updateTimers.delete(form)
      this.update(form)
    }, delay)

    this.updateTimers.set(form, timer)
  }

  async update(form) {
    if (!(form instanceof HTMLFormElement)) {
      return
    }

    const url = form.dataset.priceUrl

    if (!url) {
      return
    }

    if (!this.isConfigurationComplete(form)) {
      this.abortControllers.get(form)?.abort()

      this.hideConfigurationDetails(form)

      return
    }

    this.abortControllers.get(form)?.abort()

    const abortController = new AbortController()

    this.abortControllers.set(form, abortController)
    this.setLoading(form, true)

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: new FormData(form),
        signal: abortController.signal,
      })

      if (!response.ok) {
        throw new Error(`Product price request failed with status ${response.status}`)
      }

      const data = await response.json()

      this.render(form, data)

      if (this.changedOptionForms.has(form)) {
        this.changedOptionForms.delete(form)

        window.dispatchEvent(
          new CustomEvent('product.configuration.updated', {
            detail: {
              sku: data.sku,
              price: data.product_price,
              quantity: data.amount,
            },
          }),
        )
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return
      }

      console.error(error)
    } finally {
      if (this.abortControllers.get(form) === abortController) {
        this.abortControllers.delete(form)
        this.setLoading(form, false)
      }
    }
  }

  hideConfigurationDetails(form) {
    form.querySelector(PRICE_CONTAINER_SELECTOR)?.classList.add('d-none')

    form.querySelector(AVAILABILITY_SELECTOR)?.classList.add('d-none')

    this.toggleStockReminder(form, false)
  }

  render(form, data) {
    form.querySelector(PRICE_CONTAINER_SELECTOR)?.classList.remove('d-none')

    this.renderPrice(form, data)
    this.renderAvailability(form, data)
    this.toggleStockReminder(form, data.showStockReminder === true)
    this.updateAmount(form, data)
  }

  /*
   * Toont de "Geef mij een seintje"-knop wanneer de gekozen variant niet op voorraad is.
   * Zonder opties bepaalt de server-side render de zichtbaarheid al, dus dan blijft die staan.
   */
  toggleStockReminder(form, show) {
    if (!form.querySelector(OPTION_SELECTOR)) {
      return
    }

    form.querySelector(STOCK_REMINDER_SELECTOR)?.classList.toggle('d-none', !show)
  }

  renderPrice(form, data) {
    const priceElement = form.querySelector(PRICE_SELECTOR)
    const originalPriceElement = form.querySelector(ORIGINAL_PRICE_SELECTOR)
    const discountElement = form.querySelector(DISCOUNT_SELECTOR)

    if (priceElement && data.price) {
      /*
       * De prijs bevat server-side gegenereerde markup zoals <sup>.
       */
      priceElement.innerHTML = data.price
    }

    const hasDiscount = data.discount && data.original_price && data.original_price !== data.price

    if (originalPriceElement) {
      originalPriceElement.innerHTML = hasDiscount ? data.original_price : ''

      originalPriceElement.classList.toggle('d-none', !hasDiscount)
    }

    if (discountElement) {
      discountElement.textContent = hasDiscount ? data.discount : ''

      discountElement.classList.toggle('d-none', !hasDiscount)
    }
  }

  renderAvailability(form, data) {
    const container = form.querySelector('[data-product-configurator-availability]')
    const titleElement = form.querySelector('[data-product-configurator-availability-text]')
    const descriptionElement = form.querySelector('[data-product-configurator-availability-description]')

    if (!container || !titleElement || !data.availabilityInfo?.title) {
      container?.classList.add('d-none')

      return
    }

    const info = data.availabilityInfo

    titleElement.textContent = info.title

    if (descriptionElement) {
      descriptionElement.textContent = info.description || ''
      descriptionElement.classList.toggle('d-none', !info.description)
    }

    this.setAvailabilityState(container, info.status)

    container.classList.remove('d-none')
  }

  setAvailabilityState(container, status) {
    const icon = container.querySelector('[data-product-configurator-availability-icon]')
    const iconSymbol = container.querySelector('[data-product-configurator-availability-icon-symbol]')
    const title = container.querySelector('[data-product-configurator-availability-text]')

    if (!icon || !title) {
      return
    }

    icon.classList.remove('bg-success-subtle', 'bg-warning-subtle', 'bg-danger-subtle', 'text-success', 'text-warning', 'text-danger')

    title.classList.remove('text-success', 'text-warning', 'text-danger')

    switch (status) {
      case 'in_stock':
        icon.classList.add('bg-success-subtle', 'text-success')
        title.classList.add('text-success')

        if (iconSymbol) {
          iconSymbol.className = 'bi bi-check-lg'
        }

        break

      case 'low_stock':
        icon.classList.add('bg-warning-subtle', 'text-warning')
        title.classList.add('text-warning')

        if (iconSymbol) {
          iconSymbol.className = 'bi bi-exclamation-lg'
        }

        break

      case 'delivery_time':
        icon.classList.add('bg-warning-subtle', 'text-warning')
        title.classList.add('text-warning')

        if (iconSymbol) {
          iconSymbol.className = 'bi bi-clock'
        }

        break
    }
  }

  isConfigurationComplete(form) {
    const requiredOptions = [...form.querySelectorAll(`${OPTION_SELECTOR}[data-product-configurator-required="true"]`)]

    const radioOptions = requiredOptions.filter((option) => option instanceof HTMLInputElement && option.type === 'radio')
    const otherOptions = requiredOptions.filter((option) => !(option instanceof HTMLInputElement && option.type === 'radio'))

    for (const option of otherOptions) {
      if (!option.value) {
        return false
      }
    }

    const radioGroups = new Set(radioOptions.map((option) => option.name))

    for (const name of radioGroups) {
      const selectedOption = radioOptions.find((option) => option.name === name && option.checked)

      if (!selectedOption?.value) {
        return false
      }
    }

    return true
  }

  updateAmount(form, data) {
    const amountElement = form.querySelector(AMOUNT_SELECTOR)

    if (!(amountElement instanceof HTMLInputElement) || !Number.isInteger(data.amount)) {
      return
    }

    /*
     * De backend kan het aantal bij een uitlopend product beperken
     * tot de daadwerkelijk beschikbare voorraad.
     */
    if (Number(amountElement.value) !== data.amount) {
      amountElement.value = String(data.amount)
    }
  }

  showIncompleteState(form) {
    const availabilityElement = form.querySelector(AVAILABILITY_SELECTOR)

    availabilityElement?.classList.add('d-none')
  }

  setLoading(form, isLoading) {
    form.querySelector(LOADING_SELECTOR)?.classList.toggle('d-none', !isLoading)
  }
}
